import _ from 'lodash';
import querystring from 'query-string';
import { Subject } from "rxjs";
import { extractErrorMessage } from "@netapp/bxp-design-system-react";
import { apiClient, getOccmConfigForAgent, occmClient } from "./clients";
import { trackEvent } from "./mrTracker";
import { isNcl } from './app/utils';
import externals from './externals';

export const CLUSTER_INFO_SUBJECT = new Subject();

const PAGE_NAME_FOR_SM_NAVIGATION = {
    ALERTS:  'alerts'
};

export const SM_SUPPORTED_OBJECTS = {
    SYSTEM: 'CLUSTER',
    SVM: 'SVM',
    VOLUME: 'VOLUME',
    LUN: 'LUN',
    LUN_ASA_R2: 'LUN_ASA_R2',
    NODE: 'NODE',
    LOCAL_TIER: 'AGGREGATE',
    QTREE: 'QTREE',
    SNAPMIRROR: 'SNAPMIRROR',
    MEDIATOR: 'MEDIATOR',
    NVME_NAMESPACE: 'NVME_NAMESPACE'
};

export const supportedObjects = {
    [SM_SUPPORTED_OBJECTS.SYSTEM]: {
        pageName: 'cluster.overview',
    },
    [SM_SUPPORTED_OBJECTS.LOCAL_TIER]: {
        pageName: 'storage.aggregate-details',
        params: { uuid: '-' }
    },
    [SM_SUPPORTED_OBJECTS.SVM]: {
        pageName: 'cluster.svms.svm-details',
        params: { uuid: '-' }
    },
    [SM_SUPPORTED_OBJECTS.VOLUME]: {
        pageName: 'storage.volumes.volume-details',
        params: { uuid: '-' }
    },
    [SM_SUPPORTED_OBJECTS.LUN]: {
        pageName: 'storage.luns.lun-details',
        params: { uuid: '-' }
    },
    [SM_SUPPORTED_OBJECTS.LUN_ASA_R2]: {
        pageName: 'ontapxstorage.grid.storageunit-details',
        params: { uuid: '-' }
    },
    [SM_SUPPORTED_OBJECTS.QTREE]: {
        pageName: 'storage.qtrees.qtree-details',
        params: { uuid: '-', id: 'someID' } //uuid is volUUID, and id is qtreeId
    },
    [SM_SUPPORTED_OBJECTS.SNAPMIRROR]: {
        pageName: 'protection.relationships.relationship-details',
        params: { uuid: '-' }
    },
    [SM_SUPPORTED_OBJECTS.MEDIATOR]: {
        pageName: 'protection.overview'
    },
    [SM_SUPPORTED_OBJECTS.NVME_NAMESPACE]: {
        pageName: 'storage.nvmeNamespaces.nvmeNamespaces-details',
        params: { uuid: '-' }
    }
}

/**
 * @param {} objectDetails An object containing name and type of the object
 * @param {} pageDetails An object giving uuid and other params
 * @param {} weId
 * @param {} weType
 * @param {} provider
 * @param {} isHA defaults to false
 * @param {} disableTooltip message to display if disabled
 */
export function getSystemManagerPageObject(objectDetails, pageDetails) {
    const smObj = _.clone(supportedObjects[objectDetails.type]);
    if (!smObj) {
        throw new Error('Object not suppported for navigation.');
    }

    const smURLKeys = _.keys(smObj.params);
    let pageParams;
    if (!_.isEmpty(smURLKeys)) {
        _.forEach(smURLKeys, (key) => {
            const keyVal = pageDetails[key];
            if (!keyVal) {
                throw new Error(`${key} is not provided for navigating to ${objectDetails.type} page.`);
            }
            smObj.params[key] = keyVal;
        });
        pageParams = querystring.stringify(smObj.params);
    }

    const iFrameParams = `pageName=${smObj.pageName}&${pageParams}`
    const pagePath = pageParams ? `?${iFrameParams}` : `?pageName=${smObj.pageName}`;
    return { pagePath, iFrameParams };
}

export const writeTenancyInfoToSessionStorage = (clusterUuid, tenancyInfoData) => {
    try {
        const tenancyInfoMap = { tenancyInfoData };
        sessionStorage.setItem(`sm.navigation.tenancy.info.${clusterUuid}`, JSON.stringify(tenancyInfoMap));
    } catch (err) {
        console.error('Failed to write to session storage:', err);
    }
};

export const readTenancyInfoFromSessionStorage = (clusterUuid) => {
    try {
        let tenancyInfoMap = sessionStorage.getItem(`sm.navigation.tenancy.info.${clusterUuid}`);
        if (!_.isEmpty(tenancyInfoMap)) {
            tenancyInfoMap = JSON.parse(tenancyInfoMap);
            return tenancyInfoMap.tenancyInfoData;
        }
    } catch (err) {
        console.error('Failed to read from session storage:', err);
    }
    return undefined;
};

export const getClusterTenancyInfo = (clusterUuid) => {
    const query = {
        filter: `tags.internal:bxp:metadata:clusterUuid eq '${clusterUuid}'`
    };
    return apiClient.get(`${externals.tenancyV4Backend}/resources`, { params: query });
};

/**
* This fuction emits an observer object which contains tenancy information needed to navigate System Manager in BlueXP.
@params : clusterUuid Is a mandatory input, objectType and objectId are optional.
@params : objectType refers to the object [VOLUME,SVM,AGGREGATE] etc. in which user is interested.
@params : objectId refers to the id of the object specified above.
*/
export const notifySystemManagerObservable = ({ clusterUuid, objectType, objectId, originId }) => {
    CLUSTER_INFO_SUBJECT.next({ isLoading: true });

    // For NCL, read from session storage to avoid unnecessary API call
    if (isNcl()) {
        const cachedTenancyInfo = readTenancyInfoFromSessionStorage(clusterUuid);
        if (cachedTenancyInfo) {
            CLUSTER_INFO_SUBJECT.next({ resourceInfoAvailable: true, tenancyAlertInfo: { ...cachedTenancyInfo, legacyAgentId: originId, objectType, objectId } });
            return;
        }
    }

    getClusterTenancyInfo(clusterUuid).then((response) => {
        let tenancyResponse = response.data;
        tenancyResponse = tenancyResponse?.items && _.isArray(tenancyResponse.items) ? tenancyResponse.items : [];
        const tenancyInfoData = resolveTenancyInfo(tenancyResponse);

        if (isNcl()) {
            // Save to session storage with clusterUuid as key and tenancyInfoData as value
            writeTenancyInfoToSessionStorage(clusterUuid, tenancyInfoData);
            return Promise.resolve({
                supportedVersion: true,
                tenancyInfoData: { ...tenancyInfoData, legacyAgentId: originId }
            });
        }

        if (!tenancyInfoData.agentandWeIdMap) return Promise.reject('Insufficient tenancy information obtained for this resource.');
        // If resource class is on-prem cluster, then fetch the cluster properties to check if System manager version is compatible
        // with BlueXP. If resource class is not on-prem cluster, then proceed without version check. 
        if (tenancyInfoData.resourceClass === 'ON_PREM') {
            const clusterFromSessionStorage = getClusterFromSessionStorage(clusterUuid);
            if(_.isUndefined(clusterFromSessionStorage)) {
                return getOnPremClusterProperties(tenancyInfoData, clusterUuid, originId);
            } else {
                return clusterFromSessionStorage.isSmSupported ?
                    Promise.resolve({
                        supportedVersion: true,
                        tenancyInfoData: { ...tenancyInfoData, legacyAgentId: clusterFromSessionStorage.legacyAgentId }
                    })
                    :
                    Promise.reject('The ONTAP cluster could not be loaded due to incompatible lower version.');
            }
        } else {
            return Promise.resolve({ supportedVersion: true, tenancyInfoData });
        }
    }).then((result) => {
        if (result.supportedVersion) {
            CLUSTER_INFO_SUBJECT.next({ resourceInfoAvailable: true, tenancyAlertInfo: { ...result.tenancyInfoData, objectType, objectId } });
        }
    }).catch(ex => {
        CLUSTER_INFO_SUBJECT.next({ type: 'error', error: extractErrorMessage(ex) });
        CLUSTER_INFO_SUBJECT.next({ isLoading: false });
        console.error(ex);
    });
};

/**
 * 
 * @param {*} tenancyInfo contains the information which represents an object in System Manager
 * @returns The  properties for On Premise Clusters. This includes the system Id of the cluster, type of system and page to which user 
 * will be navigated
 */
export const getSMPageObjectfromTenancy = (tenancyInfo) => {
    const smOBJs = supportedObjects[tenancyInfo.objectType];
    const objectDetails = { type: tenancyInfo.objectType };
    let pageDetails;
    if (!_.isEmpty(smOBJs.params)) {
        pageDetails = { uuid: tenancyInfo.objectId };
        if (!_.isEmpty(smOBJs.params?.id)) {
            pageDetails['id'] = smOBJs.params.id;
        }
    };
    const pageObjectDetails = getSystemManagerPageObject(objectDetails, pageDetails, tenancyInfo.weId);
    return {
        weId: tenancyInfo.weId, type: tenancyInfo.resourceType, provider: tenancyInfo.resourceClass,
        legacyAgentId: tenancyInfo.legacyAgentId, ...pageObjectDetails
    };
};

/**
 * 
 * @param {*} history which is a React hook to manage the page URL,
 * @param { } tenancyAlertInfo containing details of the tenancy object
 * This function will launch the respective System Manager page w.r.t to the tenancy information provided
 */
export const launchSystemManagerPage = ({ history, tenancyAlertInfo, pageName=PAGE_NAME_FOR_SM_NAVIGATION.ALERTS }) => {
    const { weId, type, provider, pagePath, iFrameParams, legacyAgentId } = getSMPageObjectfromTenancy(tenancyAlertInfo);
    let actionName;
    if(pageName === PAGE_NAME_FOR_SM_NAVIGATION.ALERTS){
        actionName = 'Alert-SM-Navigation';
    } 
    if(!actionName) {
        actionName = `${pageName}-SM-Navigation`;
    }
    trackEvent({ category: 'SM-navigation', action: actionName, label: pagePath });
    history.push({
        pathname: `/system-manager/${weId}`,
        search: pagePath || '',
        state: {
            workingEnvironmentId: weId,
            type,
            provider,
            withConnector: true,
            connectorId: legacyAgentId,
            iframeParams: iFrameParams,
            navigateBackURL: `${window.location.pathname}${window.location.search ? window.location.search : ''}`,
        }
    });
}

/**
 * Opens the System Manager page in a new browser tab.
 *
 * Unlike `launchSystemManagerPage` which uses react-router `history.push` (and can pass
 * data via `location.state`), a new tab is a fresh document with no in-memory state.
 * To preserve the context the target route needs (working environment id, type, provider,
 * connector id, page name, object uuid, etc.) we encode them as URL query parameters so the
 * `/system-manager/:weId` route can hydrate from `location.search` on the new tab.
 *
 * The resulting URL is shaped like:
 *   /system-manager/<weId>?pageName=<sm.page>&uuid=<objectId>&workingEnvironmentId=<weId>&type=<type>&provider=<provider>&connectorId=<agent>&withConnector=true&iframeParams=<encodedIframeParams>
 * which the System Manager iframe consumes to navigate to its internal route (e.g. for SVMs:
 *   /sysmgr/v4/cluster/svms/<svmId>). `iframeParams` is the same descriptor that
 * `launchSystemManagerPage` passes via `location.state` so both flows hydrate the iframe identically.
 *
 * @param {Object} params
 * @param {Object} params.tenancyAlertInfo Tenancy info describing the target object (cluster/svm/volume/etc.)
 * @param {string} [params.pageName] Tracking label for the originating page; defaults to alerts.
 * @returns {Window|null} The window reference returned by `window.open`, or null if blocked.
 */
export const launchSystemManagerPageInNewTab = ({ tenancyAlertInfo, pageName = PAGE_NAME_FOR_SM_NAVIGATION.ALERTS }) => {
    const { weId, type, provider, pagePath, iFrameParams, legacyAgentId } = getSMPageObjectfromTenancy(tenancyAlertInfo);

    const actionName = pageName === PAGE_NAME_FOR_SM_NAVIGATION.ALERTS ? 'Alert-SM-Navigation-NewTab' : `${pageName}-SM-Navigation-NewTab`;
    trackEvent({ category: 'SM-navigation', action: actionName, label: pagePath });

    // These mirror the keys hydrated from `location.search` by ServiceIntegration when
    // the destination tab has no `location.state` (i.e. fresh tab). `iframeParams` mirrors
    // what `launchSystemManagerPage` passes via route state and carries the SM internal
    // route descriptor (e.g. "pageName=cluster.svms.svm-details&uuid=<svmId>") so the SM
    // iframe lands directly on /sysmgr/v4/cluster/svms/<svmId> instead of the default page.
    const stateParams = {
        workingEnvironmentId: weId,
        type,
        provider,
        withConnector: true,
        connectorId: legacyAgentId,
        iframeParams: iFrameParams
    };

    const extraQuery = _.map(stateParams, (value, key) => {
        if (_.isUndefined(value) || _.isNull(value)) return null;
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    }).filter(Boolean).join('&');

    // pagePath already starts with "?" (e.g. "?pageName=cluster.svms.svm-details&uuid=<svmId>")
    // and carries the SM internal route info. Merge it with the route-state params.
    const basePath = `/system-manager/${weId}`;
    const search = pagePath ? `${pagePath}&${extraQuery}` : `?${extraQuery}`;
    const url = `${window.location.origin}${basePath}${search}`;

    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) {
        // Defensive: when 'noopener' is honored, opener is already null. This guards older browsers.
        newWindow.opener = null;
    } else {
        console.warn('Failed to open System Manager in a new tab. The browser may have blocked the popup.');
    }
    return newWindow;
};


// This is the ONTAP version parsing utility. ONTAP has major, minor and patch.
const parseONTAPVersion = (ver) => {
    let results = (/.*?(\d+)\.(\d+)RC(\d)/).exec(ver);
    if (results) return [results[1], results[2], -2, results[3]];

    results = (/.*?(\d+)\.(\d+)D(\d)/).exec(ver);
    if (results) return [results[1], results[2], -1, results[3]];

    results = (/.*?(\d+)\.(\d+)P(\d)/).exec(ver);
    if (results) return [results[1], results[2], 1, results[3]];

    results = (/.*?(\d+)\.(\d+).*/).exec(ver);
    if (results) return [results[1], results[2], 0, 0];

    return [0, 0, 0, 0];
}

/**
 * Returns 1, 0 or -1 is the other version is smaller, same or bigger than this
 */
export const compareONTAPVersion = (ver1, ver2) => {
    const parsed1 = parseONTAPVersion(ver1);
    const parsed2 = parseONTAPVersion(ver2);

    const diff = parsed1[0] - parsed2[0] ||
        parsed1[1] - parsed2[1] ||
        parsed1[2] - parsed2[2] ||
        parsed1[3] - parsed2[3];

    return diff === 0 ? 0 : (diff > 0 ? 1 : -1);
}

export const resolveTenancyInfo = (tenancyInfo) => {
    const params = { agentandWeIdMap: {}, resourceClass: undefined, resourceType: undefined };
    if (tenancyInfo.length <= 0) return {};
    _.forEach(tenancyInfo, (tenant) => {
        params.resourceType = tenant.resourceType ?? params.resourceType;
        params.resourceClass = tenant.resourceClass ?? params.resourceClass;
        let workEnvironmentId, agentId;
        _.forEach(tenant.tags || [], (tag) => {
            workEnvironmentId = tag["internal:bxp:resourceIdentifier"] ?? workEnvironmentId;
            agentId = tag["internal:bxp:agentId"] ?? agentId;
        });
        params.weId = workEnvironmentId;
        params.agentandWeIdMap[agentId] = { weId: workEnvironmentId };
    });
    if (_.some(params, _.isUndefined)) return undefined;
    return params;
};


/**
@param { } tenancyInfo containing details of the tenancy object
@param { } originId which can be a v2 agentId/v4agentId/HARVEST Id

* If originId contains ":", then it is HARVEST id, so error out from this function.
  If originId does not contain ":", then check if it is a legacy agent id by calling getv4AgentId, if getv4AgentId returns undefined
  then call getLegacyAgentId assuming that the origin id is v4 agent id
*/
const getWorkEnvironmentAndActiveAgentId = async (tenancyInfo, originId) => {
    try {
        let legacyAgentId, workingEnvironmentId, agentId;
        if (!_.includes(originId, ":")) {
            legacyAgentId = originId + 'clients';
            agentId = await getv4AgentId(legacyAgentId);
            if(!agentId){
                agentId = originId;
                legacyAgentId = await getLegacyAgentId(originId);
            }
            workingEnvironmentId = tenancyInfo.agentandWeIdMap[agentId] ? tenancyInfo.agentandWeIdMap[agentId].weId : undefined;
        } else {
            console.error('Not usable originId found in the alert: ', originId);
        }
        return legacyAgentId && workingEnvironmentId ? Promise.resolve({ legacyAgentId, workingEnvironmentId }) :
            Promise.reject({ connectorError: true, message: 'System information could not be found.' });
    } catch (ex) {
        console.error(ex);
        return Promise.reject(ex);
    }
}

const getOnPremClusterProperties = async (tenancyInfoData, clusterUuid, originId) => {
    const smQualifiedOntapVersion = "9.10.0";
    try {
       const { legacyAgentId, workingEnvironmentId } = await getWorkEnvironmentAndActiveAgentId(tenancyInfoData, originId);
       const clusterProperties = await occmClient.get(`/occm/api/ontaps/working-environments/${workingEnvironmentId}?fields=ontapClusterProperties`, getOccmConfigForAgent(legacyAgentId));
       let clusterVersionMap = sessionStorage.getItem("occm.connector.ONTAP.clusterVersion.store") ? JSON.parse(sessionStorage.getItem("occm.connector.ONTAP.clusterVersion.store")) : {};
        if (compareONTAPVersion(clusterProperties?.data?.ontapClusterProperties?.ontapVersion, smQualifiedOntapVersion) >= 0) {
            clusterVersionMap = {...clusterVersionMap, ...{[clusterUuid]: {isSmSupported: true, legacyAgentId}}};
            sessionStorage.setItem("occm.connector.ONTAP.clusterVersion.store", JSON.stringify(clusterVersionMap));
            return Promise.resolve({ supportedVersion: true, tenancyInfoData: {...tenancyInfoData, legacyAgentId} });
        } else {
            clusterVersionMap = {...clusterVersionMap, ...{[clusterUuid]: {isSmSupported: false, legacyAgentId}}};
            sessionStorage.setItem("occm.connector.ONTAP.clusterVersion.store", JSON.stringify(clusterVersionMap))
            return Promise.reject('The ONTAP cluster could not be loaded due to incompatible lower version.');
        }
    } catch (ex) {
        console.error(ex);
        if (ex.connectorError) {
            return Promise.reject(ex.message);
        }
        return Promise.reject('The ONTAP cluster failed to return required resource information.');
    }
};

const getv4AgentId = async (legacyAgentId) => {
    const query = { filter: `tags.internal:bxp:legacyId eq '${legacyAgentId}'` };
    try {
        const response = await apiClient.get(`${externals.tenancyV4Backend}/users`, { params: query });
        return _.get(response, 'data.items[0].id', undefined);
    } catch(ex) {
        console.error(ex);
        return undefined;
    }
};

const getLegacyAgentId = async (agentId) => {
    try {
        let legacyAgentId;
        const response = await apiClient.get(`${externals.tenancyV4Backend}/users/${agentId}`);
        _.forEach(response?.data?.tags || [], (tag) => {
            legacyAgentId = tag["internal:bxp:legacyId"] ?? legacyAgentId;
        });
        return legacyAgentId;
    } catch(ex) {
        console.error(ex);
        return undefined;
    }
}

const getClusterFromSessionStorage = (clusterUuid) => {
    let clusterVersionMap = sessionStorage.getItem("occm.connector.ONTAP.clusterVersion.store");
    if(!_.isEmpty(clusterVersionMap)) {
        clusterVersionMap = JSON.parse(clusterVersionMap);    
        return clusterVersionMap[clusterUuid];
    }
    return undefined;
}

export const getWeIdForCluster = async (clusterUuid) => {
    try {
        const cachedData = readTenancyInfoFromSessionStorage(clusterUuid);
        if (cachedData) {
            return cachedData?.weId;
        }

        const response = await getClusterTenancyInfo(clusterUuid);
        let tenancyResponse = response.data;
        tenancyResponse = tenancyResponse?.items && _.isArray(tenancyResponse.items) ? tenancyResponse.items : [];
        const tenancyInfoData = resolveTenancyInfo(tenancyResponse);
        writeTenancyInfoToSessionStorage(clusterUuid, tenancyInfoData);
        
        return tenancyInfoData?.weId;
    } catch (error) {
        console.error('Error fetching weId for cluster:', clusterUuid, error);
        return null;
    }
};

/**
 * Handles a `NAVIGATE:SYSTEM-MANAGER` message sent by an embedded service (iframe or MFE)
 * and routes the parent app to the System Manager page for the requested working environment.
 *
 * @param {Object} params
 * @param {Object} params.history React router history used to perform the navigation.
 * @param {Object} params.payload Message payload from the service.
 * @param {string} params.payload.workingEnvironmentId Target working environment id (required).
 * @param {string} params.payload.type Working environment type (required).
 * @param {string} params.payload.provider Working environment provider (required).
 * @param {string} params.payload.agentId Connector to load System Manager through, when applicable.
 * @param {string} params.payload.navigateBackURL URL to navigate back to when the System Manager page is closed.
 * @returns {boolean} True when navigation was performed, false when the payload was invalid.
 */
export const navigateToSystemManagerFromService = ({ history, payload }) => {
    const { workingEnvironmentId, type, provider, agentId, navigateBackURL } = payload || {};
    if (!workingEnvironmentId || !type || !provider || !navigateBackURL) {
        console.error(`Payload must contain workingEnvironmentId, type (ON_PREM, VSA, etc.), 
            provider (ON_PREM, AWS, etc.), and navigateBackURL (relative path of the service page, ex. /serviceName/pageName, this path will be used 
            to navigate back to the service page when the System Manager page is closed)`, payload);
        return false;
    }

    const urlState = {
        workingEnvironmentId,
        type,
        provider,
        navigateBackURL
    };
    if (agentId) {
        urlState.connectorId = agentId;
        urlState.withConnector = true;
    }

    trackEvent({ category: 'SM-navigation', action: 'Service-SM-navigation', label: window.location.pathname });
    history.push({
        pathname: `/system-manager/${workingEnvironmentId}`,
        state: urlState
    });
    return true;
};
